import { z } from "zod";

export type PatientFormMode = "create" | "edit";

/** [tenant setting key, form field, label] for plain "must not be blank" fields. */
const SIMPLE_REQUIRED_TEXT_FIELDS = [
  ["middle_name", "middleName", "Middle name"],
  ["pronouns", "pronouns", "Pronouns"],
  ["preferred_name", "preferredName", "Preferred name"],
  ["preferred_provider", "preferredProviderId", "Preferred provider"],
] as const;

export interface PatientValidationConfig {
  mode: PatientFormMode;
  tenantRequiredFields?: string[];
  phoneNumberRequired?: boolean;
  existingPatientData?: {
    email?: string | null;
    dateOfBirth?: string | null;
    externalId?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    leadSource?: string | null;
    primaryLocationId?: string | null;
  };
}

/**
 * Creates a Zod schema for patient forms based on context
 */
export function createPatientValidationSchema(config: PatientValidationConfig) {
  const {
    mode,
    tenantRequiredFields = [],
    existingPatientData,
    phoneNumberRequired = true,
  } = config;

  // Base schema - all fields optional by default
  // Transform null values to empty strings to handle API responses
  const baseSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z
      .nullish(z.string())
      .transform((val) => val ?? "")
      .refine(
        (val) => val === "" || z.string().email().safeParse(val).success,
        "Invalid email",
      ),
    phoneNumber: phoneNumberRequired
      ? z.string().min(10, "Phone number is required")
      : z.nullish(z.string()).transform((val) => val ?? ""),
    dateOfBirth: z
      .union([z.string(), z.null()])
      .transform((val) => (val === null || val === "" ? undefined : val))
      .optional(),
    externalId: z.nullish(z.string()).transform((val) => val ?? ""),
    gender: z.nullish(z.string()).transform((val) => val ?? ""),
    middleName: z.nullish(z.string()).transform((val) => val ?? ""),
    pronouns: z.nullish(z.string()).transform((val) => val ?? ""),
    preferredName: z.nullish(z.string()).transform((val) => val ?? ""),
    preferredProviderId: z.nullish(z.string()).transform((val) => val ?? ""),
    address: z.nullish(z.string()).transform((val) => val ?? ""),
    addressLineTwo: z.nullish(z.string()).transform((val) => val ?? ""),
    city: z.nullish(z.string()).transform((val) => val ?? ""),
    state: z.nullish(z.string()).transform((val) => val ?? ""),
    zipCode: z.nullish(z.string()).transform((val) => val ?? ""),
    primaryLocationId: z.nullish(z.string()).transform((val) => val ?? ""),
    leadSource: z.nullish(z.string()).transform((val) => val ?? ""),
    optedInToSmsMarketing: z.boolean().nullish(),
    optedInToEmailMarketing: z.boolean().nullish(),
    optedInToSocialMedia: z.boolean().nullish(),
  });

  return baseSchema.superRefine((data, ctx) => {
    const hasChanges =
      (data.address ?? "") !== (existingPatientData?.address ?? "") ||
      (data.city ?? "") !== (existingPatientData?.city ?? "") ||
      (data.state ?? "") !== (existingPatientData?.state ?? "") ||
      (data.zipCode ?? "") !== (existingPatientData?.zipCode ?? "");

    // ============================================
    // CREATE MODE: Enforce tenant required fields
    // ============================================
    if (mode === "create") {
      // Email
      if (tenantRequiredFields.includes("email") && !data.email) {
        ctx.addIssue({
          code: "custom",
          path: ["email"],
          message: "Email is required",
        });
      }

      // Date of birth
      if (tenantRequiredFields.includes("date_of_birth") && !data.dateOfBirth) {
        ctx.addIssue({
          code: "custom",
          path: ["dateOfBirth"],
          message: "Date of birth is required",
        });
      }

      // Text fields with no validation of their own beyond being present.
      for (const [settingKey, path, label] of SIMPLE_REQUIRED_TEXT_FIELDS) {
        if (tenantRequiredFields.includes(settingKey) && !data[path]) {
          ctx.addIssue({
            code: "custom",
            path: [path],
            message: `${label} is required`,
          });
        }
      }

      // Gender
      if (tenantRequiredFields.includes("gender") && !data.gender) {
        ctx.addIssue({
          code: "custom",
          path: ["gender"],
          message: "Gender is required",
        });
      }

      // External ID
      if (tenantRequiredFields.includes("external_id") && !data.externalId) {
        ctx.addIssue({
          code: "custom",
          path: ["externalId"],
          message: "External ID is required",
        });
      }

      // Lead Source
      if (tenantRequiredFields.includes("lead_source") && !data.leadSource) {
        ctx.addIssue({
          code: "custom",
          path: ["leadSource"],
          message: "Lead source is required",
        });
      }

      // Primary Location
      if (
        tenantRequiredFields.includes("primary_location") &&
        !data.primaryLocationId
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["primaryLocationId"],
          message: "Primary location is required",
        });
      }

      // Marketing preferences
      if (tenantRequiredFields.includes("marketing")) {
        if (data.optedInToSmsMarketing == null) {
          ctx.addIssue({
            code: "custom",
            path: ["optedInToSmsMarketing"],
            message: "SMS marketing preference is required",
          });
        }

        if (data.optedInToEmailMarketing == null) {
          ctx.addIssue({
            code: "custom",
            path: ["optedInToEmailMarketing"],
            message: "Email marketing preference is required",
          });
        }
      }
    }

    // ============================================
    // EDIT MODE: Only require if field was previously filled
    // ============================================
    if (mode === "edit" && existingPatientData) {
      // Email: Only require if it was previously set AND tenant requires it
      if (
        tenantRequiredFields.includes("email") &&
        existingPatientData.email &&
        !data.email
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["email"],
          message: "Email is required",
        });
      }

      // Date of birth: Only require if it was previously set AND tenant requires it
      if (
        tenantRequiredFields.includes("date_of_birth") &&
        existingPatientData.dateOfBirth &&
        !data.dateOfBirth
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["dateOfBirth"],
          message: "Date of birth is required",
        });
      }

      // External ID: Only require if it was previously set AND tenant requires it
      if (
        tenantRequiredFields.includes("external_id") &&
        existingPatientData.externalId &&
        !data.externalId
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["externalId"],
          message: "External ID is required",
        });
      }

      // Lead Source: Only require if it was previously set AND tenant requires it
      if (
        tenantRequiredFields.includes("lead_source") &&
        existingPatientData.leadSource &&
        !data.leadSource
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["leadSource"],
          message: "Lead source is required",
        });
      }

      // Primary Location: Only require if it was previously set AND tenant requires it
      if (
        tenantRequiredFields.includes("primary_location") &&
        existingPatientData.primaryLocationId &&
        !data.primaryLocationId
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["primaryLocationId"],
          message: "Primary location is required",
        });
      }
    }

    // ============================================
    // UNIVERSAL: Address validation
    // (Applies to both create and edit modes)
    // ============================================
    const hasAnyAddressField = !!(
      data.address?.trim() ||
      data.city?.trim() ||
      data.state?.trim() ||
      data.zipCode?.trim()
    );

    const hasCompleteAddress = !!(
      data.address?.trim() &&
      data.city?.trim() &&
      data.state?.trim() &&
      data.zipCode?.trim()
    );

    // CREATE MODE: If tenant requires address, always require complete address
    if (mode === "create" && tenantRequiredFields.includes("address")) {
      if (!hasCompleteAddress) {
        if (!data.address?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["address"],
            message: "Address is required",
          });
        }
        if (!data.city?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["city"],
            message: "City is required",
          });
        }
        if (!data.state?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["state"],
            message: "State is required",
          });
        }
        if (!data.zipCode?.trim()) {
          ctx.addIssue({
            code: "custom",
            path: ["zipCode"],
            message: "ZIP code is required",
          });
        }
      }
    }

    // If user starts filling any address field, all become required
    if (hasChanges && hasAnyAddressField && !hasCompleteAddress) {
      if (!data.address?.trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["address"],
          message: "Address is required",
        });
      }
      if (!data.city?.trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["city"],
          message: "City is required",
        });
      }
      if (!data.state?.trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["state"],
          message: "State is required",
        });
      }
      if (!data.zipCode?.trim()) {
        ctx.addIssue({
          code: "custom",
          path: ["zipCode"],
          message: "ZIP code is required",
        });
      }
      // ZIP code format validation (if provided)
      if (data.zipCode && data.zipCode.trim()) {
        if (!/^\d{5}(-\d{4})?$/.test(data.zipCode.trim())) {
          ctx.addIssue({
            code: "custom",
            path: ["zipCode"],
            message: "Invalid ZIP code",
          });
        }
      }
    }

    // Date of birth validation (if provided)
    if (data.dateOfBirth) {
      const date = new Date(data.dateOfBirth);
      if (date > new Date()) {
        ctx.addIssue({
          code: "custom",
          path: ["dateOfBirth"],
          message: "Date of birth cannot be in the future",
        });
      }
    }
  });
}
